import { Operation, JournalState, Tag } from './types'
import {
  format,
  parseISO,
  startOfWeek,
  getISOWeek
} from 'date-fns'

// Pure aggregation helpers used by the calendar day cells and the dashboard.
// Everything is derived from manually-registered operations only.

export function round(n: number, d = 2): number {
  const f = Math.pow(10, d)
  return Math.round((n + Number.EPSILON) * f) / f
}

export interface DaySummary {
  count: number
  resultPct: number
  resultMoney: number
  /** Operations that explicitly followed the plan. */
  planFollowed: number
  /** Operations that explicitly broke the plan. */
  planBroken: number
  /** Operations with no answer yet. */
  planUnknown: number
}

/** Map of YYYY-MM-DD -> summary */
export function summarizeByDay(ops: Operation[]): Record<string, DaySummary> {
  const map: Record<string, DaySummary> = {}
  for (const op of ops) {
    const s = map[op.date] ?? {
      count: 0,
      resultPct: 0,
      resultMoney: 0,
      planFollowed: 0,
      planBroken: 0,
      planUnknown: 0
    }
    s.count += 1
    s.resultPct += op.resultPct
    s.resultMoney += op.resultMoney ?? 0
    if (op.followedPlan === true) s.planFollowed += 1
    else if (op.followedPlan === false) s.planBroken += 1
    else s.planUnknown += 1
    map[op.date] = s
  }
  for (const k of Object.keys(map)) {
    map[k].resultPct = round(map[k].resultPct)
    map[k].resultMoney = round(map[k].resultMoney)
  }
  return map
}

/** Sums a set of operations into a display-ready total. */
export function totalOf(ops: Operation[]): { count: number; resultPct: number; resultMoney: number } {
  return {
    count: ops.length,
    resultPct: round(ops.reduce((sum, op) => sum + op.resultPct, 0)),
    resultMoney: round(ops.reduce((sum, op) => sum + (op.resultMoney ?? 0), 0))
  }
}

export interface DashboardStats {
  balancePct: number
  winRate: number
  profitFactor: number | null
  avgRR: number
  maxDrawdown: number
  totalOps: number
  wins: number
  losses: number
  breakeven: number
}

export function computeStats(ops: Operation[]): DashboardStats {
  const total = ops.length
  if (total === 0) {
    return {
      balancePct: 0,
      winRate: 0,
      profitFactor: null,
      avgRR: 0,
      maxDrawdown: 0,
      totalOps: 0,
      wins: 0,
      losses: 0,
      breakeven: 0
    }
  }

  const sorted = [...ops].sort((a, b) => sortKey(a) - sortKey(b))

  let wins = 0
  let losses = 0
  let breakeven = 0
  let grossWinR = 0
  let grossLossR = 0
  let balancePct = 0
  let sumR = 0

  // equity curve on cumulative pct for drawdown
  let peak = 0
  let cum = 0
  let maxDD = 0

  for (const op of sorted) {
    balancePct += op.resultPct
    sumR += op.resultR
    if (op.resultPct > 0) wins++
    else if (op.resultPct < 0) losses++
    else breakeven++

    if (op.resultR > 0) grossWinR += op.resultR
    else if (op.resultR < 0) grossLossR += Math.abs(op.resultR)

    cum += op.resultPct
    if (cum > peak) peak = cum
    const dd = peak - cum
    if (dd > maxDD) maxDD = dd
  }

  const decided = wins + losses
  const winRate = decided > 0 ? (wins / decided) * 100 : 0
  const profitFactor = grossLossR > 0 ? grossWinR / grossLossR : null

  return {
    balancePct: round(balancePct),
    winRate: round(winRate, 1),
    profitFactor: profitFactor === null ? null : round(profitFactor, 2),
    avgRR: round(sumR / total, 2),
    maxDrawdown: round(maxDD),
    totalOps: total,
    wins,
    losses,
    breakeven
  }
}

function sortKey(op: Operation): number {
  return new Date(`${op.date}T${op.entryTime || '00:00'}:00`).getTime()
}

export interface EquityPoint {
  index: number
  label: string
  equity: number
}

export function equityCurve(ops: Operation[]): EquityPoint[] {
  const sorted = [...ops].sort((a, b) => sortKey(a) - sortKey(b))
  let cum = 0
  return sorted.map((op, i) => {
    cum = round(cum + op.resultPct)
    return {
      index: i + 1,
      label: format(parseISO(op.date), 'dd MMM'),
      equity: cum
    }
  })
}

export interface Bucket {
  key: string
  label: string
  resultPct: number
  count: number
}

export function groupByMonth(ops: Operation[]): Bucket[] {
  const m: Record<string, Bucket> = {}
  for (const op of ops) {
    const d = parseISO(op.date)
    const key = format(d, 'yyyy-MM')
    const b = m[key] ?? { key, label: format(d, 'MMM yy'), resultPct: 0, count: 0 }
    b.resultPct += op.resultPct
    b.count += 1
    m[key] = b
  }
  return Object.values(m)
    .sort((a, b) => a.key.localeCompare(b.key))
    .map((b) => ({ ...b, resultPct: round(b.resultPct) }))
}

export function groupByWeek(ops: Operation[]): Bucket[] {
  const m: Record<string, Bucket> = {}
  for (const op of ops) {
    const d = parseISO(op.date)
    const wkStart = startOfWeek(d, { weekStartsOn: 1 })
    const key = format(wkStart, 'yyyy-MM-dd')
    const b =
      m[key] ??
      { key, label: `Sem ${getISOWeek(d)}`, resultPct: 0, count: 0 }
    b.resultPct += op.resultPct
    b.count += 1
    m[key] = b
  }
  return Object.values(m)
    .sort((a, b) => a.key.localeCompare(b.key))
    .map((b) => ({ ...b, resultPct: round(b.resultPct) }))
}

/** Group operations by the tag chosen inside a given category. */
export function groupByCategory(
  ops: Operation[],
  categoryId: string,
  tags: Tag[]
): Bucket[] {
  const tagById = new Map(tags.map((t) => [t.id, t]))
  const m: Record<string, Bucket> = {}
  for (const op of ops) {
    const catTags = op.tagIds
      .map((id) => tagById.get(id))
      .filter((t): t is Tag => !!t && t.categoryId === categoryId)
    if (catTags.length === 0) {
      const b = m['__none__'] ?? { key: '__none__', label: 'Sin etiqueta', resultPct: 0, count: 0 }
      b.resultPct += op.resultPct
      b.count += 1
      m['__none__'] = b
      continue
    }
    for (const t of catTags) {
      const b = m[t.id] ?? { key: t.id, label: t.label, resultPct: 0, count: 0 }
      b.resultPct += op.resultPct
      b.count += 1
      m[t.id] = b
    }
  }
  return Object.values(m)
    .map((b) => ({ ...b, resultPct: round(b.resultPct) }))
    .sort((a, b) => b.resultPct - a.resultPct)
}
